/**
 * Ladenplan (Raster): Regale und feste Elemente (Wand, Tür, Kasse, Beschriftung) liegen auf einem Gitter aus
 * `spalten × zeilen` Feldern. Ein Platz ist `{x, y, b, h}` in Feldern, oben links beginnend bei 0.
 * Rein (ohne Abhängigkeiten) – wird von Worker, SPA und PDF genutzt.
 */
import { z } from "zod";

export const PLAN_MIN = { spalten: 8, zeilen: 6 } as const;
export const PLAN_MAX = { spalten: 60, zeilen: 40 } as const;

export const PlanPlatz = z.object({
  x: z.number().int().min(0).max(PLAN_MAX.spalten - 1),
  y: z.number().int().min(0).max(PLAN_MAX.zeilen - 1),
  b: z.number().int().min(1).max(PLAN_MAX.spalten),
  h: z.number().int().min(1).max(PLAN_MAX.zeilen),
});
export type PlanPlatz = z.infer<typeof PlanPlatz>;

export const PlanElementArt = z.enum(["wand", "tuer", "kasse", "text"]);
export type PlanElementArt = z.infer<typeof PlanElementArt>;

export const PlanElement = PlanPlatz.extend({
  id: z.string().min(1).max(40),
  art: PlanElementArt,
  text: z.string().trim().max(30).default(""),
});
export type PlanElement = z.infer<typeof PlanElement>;

/** Teil der Ladeneinstellungen (`einstellungen_json.plan`). */
export const Plan = z.object({
  spalten: z.number().int().min(PLAN_MIN.spalten).max(PLAN_MAX.spalten).default(24),
  zeilen: z.number().int().min(PLAN_MIN.zeilen).max(PLAN_MAX.zeilen).default(16),
  hintergrundKey: z.string().nullable().default(null),
  elemente: z.array(PlanElement).max(200).default([]),
});
export type Plan = z.infer<typeof Plan>;
export const PLAN_STANDARD: Plan = { spalten: 24, zeilen: 16, hintergrundKey: null, elemente: [] };

/** Speichern aus dem Editor: Raster, Elemente und die Plätze aller Regale (null = nicht platziert). */
export const PlanEingabe = z.object({
  spalten: Plan.shape.spalten,
  zeilen: Plan.shape.zeilen,
  elemente: Plan.shape.elemente,
  regale: z.array(z.object({ id: z.uuid(), platz: PlanPlatz.nullable() })).max(500),
});
export type PlanEingabe = z.infer<typeof PlanEingabe>;

export const ELEMENT_TEXT: Record<PlanElementArt, string> = { wand: "Wand", tuer: "Eingang", kasse: "Kasse", text: "Beschriftung" };

export function ueberschneidetPlatz(a: PlanPlatz, b: PlanPlatz): boolean {
  return a.x < b.x + b.b && b.x < a.x + a.b && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function imRaster(p: PlanPlatz, spalten: number, zeilen: number): boolean {
  return p.x >= 0 && p.y >= 0 && p.x + p.b <= spalten && p.y + p.h <= zeilen;
}

/** Prüft Raster und Überschneidungen; liefert die erste Meldung oder null. */
export function planPruefen(e: { spalten: number; zeilen: number; elemente: PlanElement[]; regale: { id: string; nummer?: string; platz: PlanPlatz | null }[] }): string | null {
  const belegt: { name: string; platz: PlanPlatz }[] = [];
  for (const el of e.elemente) {
    if (!imRaster(el, e.spalten, e.zeilen)) return `${ELEMENT_TEXT[el.art]} liegt außerhalb des Rasters.`;
  }
  for (const r of e.regale) {
    if (!r.platz) continue;
    const name = `Regal ${r.nummer ?? ""}`.trim();
    if (!imRaster(r.platz, e.spalten, e.zeilen)) return `${name} liegt außerhalb des Rasters.`;
    const anderes = belegt.find((b) => ueberschneidetPlatz(b.platz, r.platz!));
    if (anderes) return `${name} überschneidet ${anderes.name}.`;
    const el = e.elemente.find((x) => x.art !== "text" && ueberschneidetPlatz(x, r.platz!));
    if (el) return `${name} überschneidet ${ELEMENT_TEXT[el.art]}.`;
    belegt.push({ name, platz: r.platz });
  }
  return null;
}

/** Erste freie Stelle für ein Regal der Größe b × h (zeilenweise), sonst null. */
export function freierPlatz(e: { spalten: number; zeilen: number; elemente: PlanElement[]; plaetze: PlanPlatz[] }, b = 2, h = 1): PlanPlatz | null {
  for (let y = 0; y + h <= e.zeilen; y++) {
    for (let x = 0; x + b <= e.spalten; x++) {
      const p = { x, y, b, h };
      if (e.plaetze.some((q) => ueberschneidetPlatz(p, q))) continue;
      if (e.elemente.some((q) => q.art !== "text" && ueberschneidetPlatz(p, q))) continue;
      return p;
    }
  }
  return null;
}
